/** Homepage below-the-fold: latest Spotlight & Insight previews, testimonials,
 * final call-to-action and footer — all in the dark banner atmosphere. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Star, Quote } from "lucide-react";
import api from "@/lib/api";
import GoldCurve from "@/components/home/GoldCurve";
import LegalFooter from "@/components/brand/LegalFooter";

const EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: EASE },
};

const Overline = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#E7B54D]">{children}</div>
);

const PreviewCard = ({ to, eyebrow, image, category, title, summary, cta, testid }) => (
  <motion.div {...fadeUp}>
    <Link
      to={to}
      data-testid={testid}
      className="group block bg-[#221709] border border-[#C28A2E]/20 rounded-2xl overflow-hidden hover:border-[#C28A2E]/60 transition-colors duration-500"
    >
      {image && (
        <div className="overflow-hidden">
          <img src={image} alt={title} className="w-full aspect-[21/9] object-cover opacity-90 transition-transform duration-700 ease-out group-hover:scale-105" />
        </div>
      )}
      <div className="p-7 sm:p-8">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] font-bold">
          <span className="text-[#E7B54D]">{eyebrow}</span>
          {category && (
            <>
              <span className="w-1 h-1 rounded-full bg-[#C28A2E]/60" />
              <span className="text-[#F0E6D2]/55">{category}</span>
            </>
          )}
        </div>
        <h3 className="mt-3 font-display text-xl sm:text-2xl font-medium text-[#FDFBF5] leading-snug transition-colors duration-300 group-hover:text-[#E7B54D]">
          {title}
        </h3>
        <p className="mt-2 text-sm text-[#F0E6D2]/60 leading-relaxed line-clamp-2">{summary}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#E7B54D]">
          {cta}
          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  </motion.div>
);

export default function HomeBelowFold() {
  const [spotlight, setSpotlight] = useState(null);
  const [insight, setInsight] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/public/spotlights").then((r) => setSpotlight((r.data.spotlights || [])[0] || null)).catch(() => {});
    api.get("/public/insights").then((r) => setInsight((r.data.insights || [])[0] || null)).catch(() => {});
    api.get("/public/testimonials").then((r) => setTestimonials((r.data || []).filter((t) => t.enjoyed_most).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <>
      {/* ============== Latest from the movement ============== */}
      {(spotlight || insight) && (
        <section className="relative max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-28" data-testid="home-latest-section">
          <motion.div {...fadeUp} className="text-center mb-12">
            <Overline>Fresh from the movement</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-light text-[#FDFBF5]">The latest stories &amp; thinking</h2>
          </motion.div>
          <div className={`grid grid-cols-1 ${spotlight && insight ? "md:grid-cols-2" : ""} gap-6 lg:gap-8`}>
            {spotlight && (
              <PreviewCard
                to={`/spotlight/${spotlight.id}`}
                eyebrow="CCS Spotlight"
                image={spotlight.image_url}
                category={spotlight.category}
                title={spotlight.title || spotlight.honoree_name}
                summary={spotlight.summary}
                cta="Read the story"
                testid="latest-spotlight-preview"
              />
            )}
            {insight && (
              <PreviewCard
                to={`/insights/${insight.id}`}
                eyebrow="CCS Insights"
                image={insight.image_url}
                category={insight.category}
                title={insight.title}
                summary={insight.summary}
                cta="Read the insight"
                testid="latest-insight-preview"
              />
            )}
          </div>
        </section>
      )}

      {/* ============== Testimonials ============== */}
      {testimonials.length > 0 && (
        <section className="relative border-t border-[#C28A2E]/15" data-testid="home-testimonials">
          <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
            <motion.div {...fadeUp} className="text-center mb-12">
              <Overline>Kind words</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-light text-[#FDFBF5]">What participants say</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.figure
                  key={t.id}
                  {...fadeUp}
                  transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
                  className="bg-[#221709] border border-[#C28A2E]/20 rounded-2xl p-7"
                >
                  <Quote size={20} strokeWidth={1.5} className="text-[#C28A2E]" />
                  <div className="flex gap-1 mt-4">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13} className={n <= (t.star_rating || 0) ? "fill-[#E7B54D] text-[#E7B54D]" : "text-[#5D3A1F]"} strokeWidth={1.5} />
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-[#F0E6D2]/80 italic line-clamp-4">“{t.enjoyed_most}”</blockquote>
                  <figcaption className="mt-5">
                    <div className="font-display font-medium text-[#FDFBF5] text-sm">{t.display_name}</div>
                    {t.organisation && <div className="text-xs text-[#F0E6D2]/50 mt-0.5">{t.organisation}</div>}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
            <motion.div {...fadeUp} className="text-center mt-10">
              <Link to="/testimonials" className="text-sm text-[#E7B54D] hover:text-[#F5D88A] font-semibold tracking-wide transition-colors" data-testid="home-all-testimonials-link">
                Read all testimonials →
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ============== Final CTA ============== */}
      <section className="relative overflow-hidden border-t border-[#C28A2E]/15" data-testid="home-final-cta">
        <GoldCurve offsetY={180} className="opacity-80" />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 py-24 sm:py-32 text-center">
          <motion.div {...fadeUp}>
            <Overline>Begin your journey</Overline>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#FDFBF5] leading-tight">
              Ready to build a stronger team?
            </h2>
            <p className="mt-5 text-[#F0E6D2]/65 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
              Start with a free conversation, or tell us about your team and receive a proposal
              shaped around your exact context.
            </p>
            <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/book#booking"
                className="bg-gradient-to-b from-[#E7B54D] to-[#C28A2E] text-[#2A1810] px-8 py-4 rounded-full text-sm font-bold tracking-wide hover:from-[#F5D88A] hover:to-[#D99A32] transition-colors duration-300 shadow-[0_8px_30px_rgba(194,138,46,0.25)]"
                data-testid="cta-book-btn"
              >
                Book a Discovery Call
              </Link>
              <Link
                to="/discover"
                className="border border-[#F0E6D2]/30 text-[#FDFBF5] px-8 py-4 rounded-full text-sm font-semibold tracking-wide hover:border-[#E7B54D] hover:text-[#E7B54D] transition-colors duration-300"
                data-testid="cta-assessment-btn"
              >
                Take the Needs Assessment
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============== Footer ============== */}
      <footer className="border-t border-[#C28A2E]/15" data-testid="home-footer">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 text-center">
          <div className="font-display text-[#FDFBF5] text-lg tracking-wide">The CCS Approach</div>
          <div className="mt-2 text-[11px] uppercase tracking-[0.28em] text-[#E7B54D] font-semibold">
            Communicate · Collaborate · Serve
          </div>
          <nav className="mt-6 flex items-center justify-center gap-x-6 gap-y-2 flex-wrap text-xs text-[#F0E6D2]/55">
            <Link to="/book#about" className="hover:text-[#E7B54D] transition-colors">About</Link>
            <Link to="/book#philosophy" className="hover:text-[#E7B54D] transition-colors">Framework</Link>
            <Link to="/book#services" className="hover:text-[#E7B54D] transition-colors">Services</Link>
            <Link to="/spotlight" className="hover:text-[#E7B54D] transition-colors">Spotlight</Link>
            <Link to="/insights" className="hover:text-[#E7B54D] transition-colors">Insights</Link>
            <Link to="/testimonials" className="hover:text-[#E7B54D] transition-colors">Testimonials</Link>
            <Link to="/book#booking" className="hover:text-[#E7B54D] transition-colors">Contact</Link>
          </nav>
          <div className="mt-6 text-xs text-[#F0E6D2]/50">
            <a href="mailto:theccsapproach@gmail.com" className="hover:text-[#E7B54D] transition-colors">theccsapproach@gmail.com</a>
            <span className="mx-2">·</span> 868 752 9924
          </div>
          <p className="mt-7 text-xs italic text-[#C9A56A]">"Let everything you do be done in love." — 1 Corinthians 16:14</p>
          <LegalFooter dark className="mt-7" />
        </div>
      </footer>
    </>
  );
}
